import React, { useContext, useState, useCallback, useEffect } from 'react';
import './src-components-Chat-Chat.styles.scss.js';
import ChatButton from './src-components-ChatButton-ChatButton.js';
import ChatWindow from './src-components-ChatWindow-ChatWindow.js';
import { CAContext } from './src-contexts-CAContext.js';
import { useCustomTracking } from './src-hooks-custom-hooks-useTracking.js';
import { useUtilityHooks } from './src-hooks-useUtilityHooks.js';
import { useMIAWHooks } from './src-hooks-useMIAWHooks.js';
import { useChatbotHooks } from './src-hooks-useChatbotHook.js';
import { setUnreadCount, setChatWindowState } from './src-utils-session-storage-utils.js';
import { CHAT_BUTTON_TEXT, CHAT_HEADER_TEXT } from './src-config-app-config.js';
import { MiawChatContext } from './src-contexts-MiawChatContext.js';
import { useCustomChatEvents } from './src-hooks-custom-hooks-useCustomChatEvents.js';
import { CaseDataContext } from './src-contexts-CaseDataContext.js';
import { useNRLogging } from './src-hooks-custom-hooks-useNRLogging.js';

const Chat = ({ onChatButtonClick }) => {
  const { logCustomAttributesNR, logChat } = useNRLogging();
  const { sendChatEvents, handleAddEventListeners, addOpenChatEventListener } = useCustomChatEvents();
  const {
    state: { caseData }
  } = useContext(CaseDataContext);
  const {
    chatState: { chatWindow, chatSession, unreadMessages, enableChatbot, gcpChatData, isChatEventListenersAdded },
    setChatState
  } = useContext(CAContext);
  const {
    state: { agentName }
  } = useContext(MiawChatContext);
  const { sendTrackingEvent } = useCustomTracking();
  const [pageURL, setPageURL] = useState("");
  const {
    utilityHooksLoading: { gcpChatLoading, userInfoLoading }
  } = useUtilityHooks();
  const {
    miawHooksData: { miawChatRetrieveData, miawChatStartData },
    miawHooksTriggers: { miawChatRetrieveTrigger }
  } = useMIAWHooks();
  const {
    chatbotHooksData: { chatbotRetrieveData, chatbotStartData },
    chatbotHooksTriggers: { triggerChatbotRetrieve }
  } = useChatbotHooks();
  const onButtonClick = useCallback(() => {
    if (enableChatbot || chatSession === "chatbotSession") {
      sendTrackingEvent("Chatbot Clicked", "Chatbot:CTA", "chatCtaClicked");
      logCustomAttributesNR("ca-chatbot-info", {
        logName: "chatbot_clicked",
        logLevel: "info",
        message: "Chatbot clicked"
      });
    } else {
      sendTrackingEvent("Chat Initiated", "Chat help initiated - Chat Anywhere", `${caseData?.pagePath};Chat Initiate`, "Global Chat");
      logChat("CA_HELP_BUTTON_CLICKED", {});
    }
    setChatState((prevState) => ({
      ...prevState,
      chatWindow: "maximized"
    }));
    if (onChatButtonClick) {
      onChatButtonClick();
    }
  }, [onChatButtonClick, enableChatbot, chatSession, caseData]);
  useEffect(() => {
    if (gcpChatData) {
      handleAddEventListeners();
      addOpenChatEventListener(onButtonClick);
    }
  }, [gcpChatData, onButtonClick]);
  useEffect(() => {
    if (isChatEventListenersAdded) {
      sendChatEvents("onChatReady", {});
    }
  }, [isChatEventListenersAdded]);
  useEffect(() => {
    if (chatSession === "miawChatSession") {
      if (!miawChatRetrieveData && !miawChatStartData) {
        miawChatRetrieveTrigger();
      }
      if (!chatbotRetrieveData && !chatbotStartData) {
        triggerChatbotRetrieve();
      }
    }
    if (chatSession === "chatbotSession" && !chatbotRetrieveData && !chatbotStartData) {
      triggerChatbotRetrieve();
    }
  }, [chatSession, miawChatRetrieveData, chatbotRetrieveData, miawChatStartData, triggerChatbotRetrieve, triggerChatbotRetrieve]);
  useEffect(() => {
    if (unreadMessages && chatWindow === "maximized") {
      setChatState((prevState) => ({
        ...prevState,
        unreadMessages: 0
      }));
    }
  }, [chatWindow, setChatState, unreadMessages]);
  useEffect(() => {
    setUnreadCount(unreadMessages || 0);
  }, [unreadMessages]);
  const [isChatEventSent, setIsChatEventSent] = React.useState(false);
  useEffect(() => {
    if (!isChatEventSent) {
      if (enableChatbot) sendTrackingEvent("Chatbot Viewed", "Chatbot:CTA", "chatCtaViewed");
      setIsChatEventSent(true);
    }
  }, [isChatEventSent, sendTrackingEvent]);
  useEffect(() => {
    if (typeof window !== "undefined") {
      setPageURL(window.location.href);
    }
  }, []);
  useEffect(() => {
    if (pageURL)
      logCustomAttributesNR("ca-chatbot-info", {
        logName: "chat_page_change",
        logLevel: "info",
        message: `Chat page changed to: ${pageURL}, session: ${chatSession}`,
        chatSession
      });
  }, [pageURL]);
  useEffect(() => {
    setChatWindowState(chatWindow);
    if (chatWindow === "maximized" && isChatEventListenersAdded) {
      sendChatEvents("onMaximize", {});
    }
  }, [chatWindow, isChatEventListenersAdded]);
  useEffect(() => {
    let btnTxt = "";
    let headerTxt = "";
    switch (chatSession) {
      case "chatbotSession":
        btnTxt = CHAT_BUTTON_TEXT.CHATBOT;
        headerTxt = CHAT_HEADER_TEXT.CHATBOT;
        break;
      case "miawChatSession":
        btnTxt = agentName && agentName !== "" ? agentName : CHAT_BUTTON_TEXT.MIAW_CHAT;
        headerTxt = agentName && agentName !== "" ? agentName : CHAT_HEADER_TEXT.MIAW_CHAT;
        break;
      default:
        btnTxt = enableChatbot ? CHAT_BUTTON_TEXT.CHATBOT : CHAT_BUTTON_TEXT.MIAW_CHAT;
        headerTxt = enableChatbot ? CHAT_HEADER_TEXT.CHATBOT : CHAT_HEADER_TEXT.MIAW_CHAT;
        break;
    }
    setChatState((prevState) => ({
      ...prevState,
      chatButtonText: btnTxt,
      chatHeaderText: headerTxt
    }));
  }, [chatSession, enableChatbot, agentName]);
  if (gcpChatLoading || userInfoLoading) {
    return null;
  }
  if (gcpChatData?.status.isChatEnabled || pageURL?.includes("localhost") || pageURL?.includes("staging")) {
    return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(ChatButton, { onClick: onButtonClick }), /* @__PURE__ */ React.createElement(ChatWindow, null));
  }
  return null;
};

export { Chat as default };
//# sourceMappingURL=src-components-Chat-Chat.js.map
